import {
  convert,
  DayOfWeek,
  Duration,
  LocalDate,
  LocalDateTime,
  LocalTime,
  TemporalAdjusters,
  ZonedDateTime,
  ZoneId,
} from "@js-joda/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@talladega/icons";
import { Button } from "@talladega/components";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import "@js-joda/timezone/dist/js-joda-timezone-10-year-range";

export type RegularHours = {
  openTime: string | null;
  closeTime: string | null;
  openDay: string;
  closeDay: string;
};

export type SpecialHours = {
  openTime: string | null;
  closeTime: string | null;
  title: string;
  date: string;
  isClosed: boolean;
};

type Props = {
  regularHours: RegularHours[];
  specialHours: SpecialHours[];
  onAppointmentDateTimeSelected: (
    appointmentDateTime: LocalDateTime | null
  ) => void;
  minAppointmentDate: LocalDate;
  weekOfDate: LocalDate;
  existingAppointmentDateTime: LocalDateTime | null;
  unavailableTimeSlots: LocalDateTime[];
  onWeekBack: () => void;
  onWeekForward: () => void;
  timeZone: string;
};

type WeeklyCalendarDay = {
  disabled: boolean;
  id: number;
  localDate: LocalDate;
  availableTimes: LocalTime[];
};

const appointmentLength = Duration.ofMinutes(
  Number.parseInt(
    process.env.NEXT_PUBLIC_DROP_OFF_APPOINTMENT_DURATION_IN_MINUTES ?? "",
    10
  )
);

const WeeklyAppointmentTimePicker = ({
  regularHours,
  specialHours,
  onAppointmentDateTimeSelected,
  minAppointmentDate,
  weekOfDate,
  existingAppointmentDateTime,
  unavailableTimeSlots,
  onWeekBack,
  onWeekForward,
  timeZone,
}: Props) => {
  const { locale } = useRouter();
  const { t } = useTranslation(["purchase"]);
  const [selectedAppointmentDate, setSelectedAppointmentDate] =
    useState<LocalDate | null>(null);
  const [selectedAppointmentTime, setSelectedAppointmentTime] =
    useState<LocalTime | null>(null);

  const getAvailableHours = useCallback(
    (date: LocalDate, startTime: LocalTime, endTime: LocalTime) => {
      const availableTimes: LocalTime[] = [];

      // if the appointment is before the min time, we should make
      // sure the appointment time is still included, but only if it's also in the future
      if (
        existingAppointmentDateTime &&
        existingAppointmentDateTime.toLocalDate().isEqual(date) &&
        existingAppointmentDateTime.toLocalTime().isBefore(startTime) &&
        existingAppointmentDateTime.isAfter(
          ZonedDateTime.now(ZoneId.of(timeZone)).toLocalDateTime()
        )
      ) {
        availableTimes.push(existingAppointmentDateTime.toLocalTime());
      }

      for (
        let time = startTime;
        time.isBefore(endTime);
        time = time.plus(appointmentLength)
      ) {
        // since we're selecting the slot for an existing appointment, we need to make sure it's always included
        if (
          existingAppointmentDateTime &&
          existingAppointmentDateTime.equals(date.atTime(time))
        ) {
          availableTimes.push(time);
        } else if (
          !unavailableTimeSlots.some((x) => x.equals(date.atTime(time)))
        ) {
          availableTimes.push(time);
        }
      }

      return availableTimes;
    },
    [existingAppointmentDateTime, timeZone, unavailableTimeSlots]
  );

  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const createDaysOfWeek = useCallback(
    (startOfWeekGiven: LocalDate) => {
      const days: WeeklyCalendarDay[] = [];

      // if the current day is not sunday set the start from the previous sunday
      const weekStart = startOfWeekGiven.with(
        TemporalAdjusters.previousOrSame(DayOfWeek.SUNDAY)
      );

      for (let i = 0; i < 7; i += 1) {
        let availableTimes: LocalTime[] = [];
        const date = weekStart.plusDays(i);
        const currentDaysRegularHours = regularHours.find(
          (x) =>
            x.openDay.toLowerCase() === date.dayOfWeek().name().toLowerCase()
        );

        const specialHoursForCurrentDay = specialHours.find((x) =>
          LocalDate.parse(x.date).equals(date)
        );

        let openTime = currentDaysRegularHours?.openTime;
        let closeTime = currentDaysRegularHours?.closeTime;

        const isClosed =
          (!openTime && !closeTime) ||
          (specialHoursForCurrentDay && specialHoursForCurrentDay.isClosed) ||
          date.isBefore(minAppointmentDate);

        if (specialHoursForCurrentDay) {
          openTime = specialHoursForCurrentDay?.openTime;
          closeTime = specialHoursForCurrentDay?.closeTime;
        }

        if (openTime && closeTime) {
          let startTime: LocalTime;

          if (date.isEqual(minAppointmentDate)) {
            if (LocalTime.parse(openTime).isBefore(LocalTime.of(13))) {
              startTime = LocalTime.of(13, 0);
            } else {
              startTime = LocalTime.parse(openTime);
            }
          } else {
            startTime = LocalTime.parse(openTime);
          }

          const endTime = LocalTime.parse(closeTime);

          availableTimes = getAvailableHours(date, startTime, endTime);
        }

        days.push({
          id: date.dayOfWeek().value(),
          localDate: date,
          disabled: isClosed,
          availableTimes,
        });
      }

      return days;
    },
    [regularHours, specialHours, minAppointmentDate, getAvailableHours]
  );

  const createdDays = useMemo(
    () => createDaysOfWeek(weekOfDate),
    [createDaysOfWeek, weekOfDate]
  );

  const handleWeekBack = () => {
    onWeekBack();

    setSelectedAppointmentDate(null);
    setSelectedAppointmentTime(null);
    onAppointmentDateTimeSelected(null);
  };

  const handleWeekForward = () => {
    onWeekForward();

    setSelectedAppointmentDate(null);
    setSelectedAppointmentTime(null);
    onAppointmentDateTimeSelected(null);
  };

  const onAppointmentTimeClick = (appointmentTime: LocalTime) => {
    if (!selectedAppointmentDate) return;

    setSelectedAppointmentTime(appointmentTime);
    const appointmentLocalDateTime =
      selectedAppointmentDate.atTime(appointmentTime);

    onAppointmentDateTimeSelected(appointmentLocalDateTime);
  };

  const handleOnDayClick = (daySelected: LocalDate) => {
    setSelectedAppointmentDate(daySelected);

    setSelectedAppointmentTime(null);
    onAppointmentDateTimeSelected(null);
  };

  useEffect(() => {
    if (createdDays) {
      setIsFirstLoad(false);

      // basically, if we have an existing appointment in the current week which is after "now",
      // AND we don't have an otherwise selected appointment date, select the existing appointment
      if (
        selectedAppointmentDate === null &&
        existingAppointmentDateTime &&
        existingAppointmentDateTime.isAfter(
          ZonedDateTime.now(ZoneId.of(timeZone)).toLocalDateTime()
        ) &&
        existingAppointmentDateTime.toLocalDate().isAfter(weekOfDate) &&
        existingAppointmentDateTime
          .toLocalDate()
          .isBefore(weekOfDate.with(TemporalAdjusters.next(DayOfWeek.SUNDAY)))
      ) {
        setSelectedAppointmentDate(existingAppointmentDateTime.toLocalDate());
        setSelectedAppointmentTime(existingAppointmentDateTime.toLocalTime());
      }
      // if it's first load, and the week we would show doesn't have any days, go to next week
      else if (createdDays.every((d) => d.disabled) && isFirstLoad) {
        // technically only works for one week.
        // if "next" week doesn't have available slots either, this won't skip to the following week
        onWeekForward();
      }
      // otherwise, as long as we don't have a selected appointment date, just pick the first day
      else if (selectedAppointmentDate === null) {
        const firstAvailableDay = createdDays.find((d) => !d.disabled);

        if (firstAvailableDay) {
          setSelectedAppointmentDate(firstAvailableDay.localDate);
        }
      }
    }
  }, [
    createdDays,
    existingAppointmentDateTime,
    isFirstLoad,
    onWeekForward,
    selectedAppointmentDate,
    timeZone,
    weekOfDate,
  ]);

  const showWeekStartYear =
    createdDays[0].localDate.year() !== createdDays[6].localDate.year();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-evenly items-center">
        <ChevronLeftIcon
          className="cursor-pointer w-5 h-5 text-secondary"
          onClick={() => handleWeekBack()}
        />
        <p className="font-semibold text-base">
          {convert(createdDays[0].localDate)
            .toDate()
            .toLocaleDateString(locale === "en" ? "en-CA" : "fr-CA", {
              month: "long",
              day: "numeric",
              year: showWeekStartYear ? "numeric" : undefined,
            })}{" "}
          -{" "}
          {convert(createdDays[6].localDate)
            .toDate()
            .toLocaleDateString(locale === "en" ? "en-CA" : "fr-CA", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
        </p>
        <ChevronRightIcon
          className="cursor-pointer w-5 h-5 text-secondary"
          onClick={() => handleWeekForward()}
        />
      </div>
      <div className="flex justify-between items-center text-sm sm:text-base">
        {createdDays.map((content) => (
          <div
            key={`day-${content.id}`}
            className="flex flex-col justify-between items-center xs:w-full"
          >
            <p>
              {convert(content.localDate)
                .toDate()
                .toLocaleDateString(locale === "en" ? "en-CA" : "fr-CA", {
                  weekday: "short",
                })}
            </p>
            <Button
              size="small"
              onClick={() => handleOnDayClick(content.localDate)}
              className="flex justify-center items-center px-0 xs:px-1 sm:px-2 xs:w-2/3"
              buttonStyle={
                content.localDate.equals(selectedAppointmentDate)
                  ? "primary"
                  : "tertiary"
              }
              fill="filled"
              spacing="tight-hug"
              disabled={content.disabled}
            >
              <p>{content.localDate.dayOfMonth()}</p>
            </Button>
          </div>
        ))}
      </div>
      <div className="flex flex-col text-center">
        <p className="font-semibold text-base py-4">
          {t("purchase:available_hours")}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 place-items-center">
          {createdDays
            .find((d) => d.localDate.equals(selectedAppointmentDate))
            ?.availableTimes?.map((time) => (
              <Button
                key={`day-${time.toString()}`}
                size="small"
                className="flex justify-center items-center rounded-full w-full"
                buttonStyle={
                  time.equals(selectedAppointmentTime) ? "primary" : "tertiary"
                }
                fill="filled"
                spacing="tight-hug"
                onClick={() => onAppointmentTimeClick(time)}
              >
                <p>
                  {/* this usage of LocalDate.now() is fine, as it's not actually consequential */}
                  {convert(LocalDate.now().atTime(time))
                    .toDate()
                    .toLocaleTimeString(`${locale === "fr" ? "fr" : "en"}-CA`, {
                      hour: "numeric",
                      minute: "numeric",
                      hour12: locale === "en",
                    })}
                </p>
              </Button>
            ))}
        </div>
      </div>
    </div>
  );
};

export default WeeklyAppointmentTimePicker;
